// FormFoldersModal — "Manage folders": create, rename and remove form folders.
// Edits a local draft; nothing persists until "Save folders", when the draft is
// diffed against the server list and the create / rename / delete mutations fire
// concurrently. Cancel / X / Escape discard — which is why deleting a row needs
// no confirm step. Same shape as structure-page/TagsModal + FormWorkflowModal's
// saveTags, but this modal is single-use so it owns its own data and mutations.

import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Icon, IconButton, Input, Modal } from '@/components/common';
import {
  type FormFolder,
  useCreateFormFolderMutation,
  useDeleteFormFolderMutation,
  useGetFormFoldersQuery,
  useUpdateFormFolderMutation,
} from '@/api/endpoints/formFoldersApi';

/** Stable empty fallback — a fresh `[]` each render would loop the seed effect. */
const NO_FOLDERS: FormFolder[] = [];

/** A draft row — no `id` means a not-yet-created folder. */
type FolderDraft = { id?: string; title: string; count: number };

interface FormFoldersModalProps {
  open: boolean;
  onClose: () => void;
}

const FormFoldersModal = ({ open, onClose }: FormFoldersModalProps) => {
  const { t } = useTranslation();
  const { data } = useGetFormFoldersQuery(undefined, { skip: !open });
  const folders = data ?? NO_FOLDERS;
  const [createFolder] = useCreateFormFolderMutation();
  const [updateFolder] = useUpdateFormFolderMutation();
  const [deleteFolder] = useDeleteFormFolderMutation();

  const [draft, setDraft] = useState<FolderDraft[]>([]);
  const [query, setQuery] = useState('');
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Re-seed the draft each time the modal opens; discard leftovers on close.
  useEffect(() => {
    if (!open) return;
    setDraft(folders.map((f) => ({ id: f.id, title: f.title, count: f.formIds.length })));
    setQuery('');
    setEditIndex(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, folders]);

  const name = query.trim();
  const duplicate = draft.some((f) => f.title.trim().toLowerCase() === name.toLowerCase());

  const create = () => {
    if (!name || duplicate) return;
    setDraft([...draft, { title: name, count: 0 }]);
    setQuery('');
  };

  const onQueryKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      create();
    }
  };

  const startRename = (index: number) => {
    setEditIndex(index);
    setEditValue(draft[index].title);
  };

  const commitRename = () => {
    const value = editValue.trim();
    if (editIndex === null) return;
    if (value) setDraft(draft.map((f, i) => (i === editIndex ? { ...f, title: value } : f)));
    setEditIndex(null);
  };

  const onRenameKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitRename();
    } else if (e.key === 'Escape') {
      // Stop the Modal's own Escape handler — this only cancels the rename.
      e.stopPropagation();
      setEditIndex(null);
    }
  };

  const remove = (index: number) => {
    setDraft(draft.filter((_, i) => i !== index));
    setEditIndex(null);
  };

  // Diff the draft against the server list and fire everything concurrently.
  const save = async () => {
    const rows = draft.map((f) => ({ ...f, title: f.title.trim() })).filter((f) => f.title);
    const byId = new Map(folders.map((f) => [f.id, f.title]));
    const draftIds = new Set(rows.filter((f) => f.id).map((f) => f.id));
    const creates = rows.filter((f) => !f.id);
    const updates = rows.filter((f) => f.id && byId.get(f.id) !== f.title);
    const deletes = folders.filter((f) => !draftIds.has(f.id));

    if (!creates.length && !updates.length && !deletes.length) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      await Promise.all([
        ...creates.map((f) => createFolder({ title: f.title }).unwrap()),
        ...updates.map((f) =>
          updateFolder({ id: f.id as string, body: { title: f.title } }).unwrap(),
        ),
        ...deletes.map((f) => deleteFolder(f.id).unwrap()),
      ]);
      toast.success(t('forms_folders_saved'));
      onClose();
    } catch {
      toast.error(t('error_generic'));
    } finally {
      setSaving(false);
    }
  };

  // The create input doubles as a search — rows narrow as you type; the original
  // index rides along so rename/delete still hit the right row.
  const shown = useMemo(() => {
    const q = name.toLowerCase();
    return draft
      .map((folder, index) => ({ folder, index }))
      .filter(({ folder }) => !q || folder.title.toLowerCase().includes(q));
  }, [draft, name]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      className='form-folders'
      icon='folder'
      title={t('forms_folders_title')}
      subtitle={t('forms_folders_subtitle', { count: draft.length })}
      footer={
        <>
          <p className='form-folders__note'>
            <Icon name='info' size={15} aria-hidden='true' />
            {t('forms_folders_note')}
          </p>
          <div className='form-folders__footer-actions'>
            <Button variant='secondary' onClick={onClose} disabled={saving}>
              {t('button_cancel')}
            </Button>
            <Button iconStart='check' onClick={save} loading={saving}>
              {t('forms_folders_save')}
            </Button>
          </div>
        </>
      }
    >
      <div className='form-folders__create'>
        <Input
          iconStart='search'
          placeholder={t('forms_folders_search_ph')}
          aria-label={t('forms_folders_search_ph')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onQueryKeyDown}
        />
        <Button iconStart='folder-plus' disabled={!name || duplicate} onClick={create}>
          {t('forms_folders_create')}
        </Button>
      </div>

      {shown.length > 0 ? (
        <ul className='form-folders__list'>
          {shown.map(({ folder, index }) => (
            // Index keys are safe here: rows are only appended/removed via the
            // draft handlers and carry no identity beyond their position.
            <li key={index} className='form-folders__row'>
              <Icon name='folder' size={18} className='form-folders__row-icon' aria-hidden='true' />
              {editIndex === index ? (
                <>
                  <Input
                    size='sm'
                    autoFocus
                    className='form-folders__row-input'
                    aria-label={t('forms_folders_rename')}
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={onRenameKeyDown}
                  />
                  <IconButton
                    icon='check'
                    variant='ghost'
                    size='sm'
                    aria-label={t('forms_folders_rename_save')}
                    onClick={commitRename}
                  />
                  <IconButton
                    icon='x'
                    variant='ghost'
                    size='sm'
                    aria-label={t('forms_folders_rename_undo')}
                    onClick={() => setEditIndex(null)}
                  />
                </>
              ) : (
                <>
                  <span className='form-folders__row-title'>{folder.title}</span>
                  <span
                    className='form-folders__row-count'
                    aria-label={t('forms_folders_count', { count: folder.count })}
                  >
                    <Icon name='clipboard-list' size={15} aria-hidden='true' />
                    {folder.count}
                  </span>
                  <IconButton
                    icon='pencil'
                    variant='ghost'
                    size='sm'
                    aria-label={t('forms_folders_rename')}
                    onClick={() => startRename(index)}
                  />
                  <IconButton
                    icon='trash-2'
                    variant='ghost'
                    size='sm'
                    className='form-folders__delete'
                    aria-label={t('forms_folders_delete')}
                    onClick={() => remove(index)}
                  />
                </>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className='form-folders__empty'>
          <Icon name='folder' size={16} aria-hidden='true' />
          {t('forms_folders_modal_empty')}
        </p>
      )}
    </Modal>
  );
};

export default FormFoldersModal;
